import { Alert, SkeletonRows, useToast } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape, useAccountingCompany } from "../../../../features/accounting/company.js";
import { CounterpartyForm } from "../../../../features/counterparties/counterparty-form.js";
import { useCounterpartyCard } from "../../../../features/counterparties/detail/use-card.js";
import { CounterpartyPage, useListCrumb } from "../../../../features/counterparties/shared.js";
import { errorMessage } from "../../../../lib/errors.js";

export const Route = createFileRoute("/_app/cari/$id/duzenle")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: EditCounterpartyPage,
});

function EditCounterpartyPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const listCrumb = useListCrumb();
  const { companies } = useAccountingCompany();
  const card = useCounterpartyCard(id);
  const title = card.data ? t("counterparties.form.editTitle", { code: card.data.code }) : t("common.edit");
  return (
    <CounterpartyPage
      title={title}
      breadcrumb={[
        listCrumb,
        ...(card.data
          ? [
              {
                label: card.data.code,
                link: (children: ReactNode, className: string) => (
                  <Link to="/cari/$id" params={{ id }} className={className}>
                    {children}
                  </Link>
                ),
              },
            ]
          : []),
        { label: t("common.edit") },
      ]}
    >
      {({ companyId }) =>
        card.error ? (
          <Alert tone="danger">{errorMessage(card.error)}</Alert>
        ) : !card.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <CounterpartyForm
            key={card.data.id}
            card={card.data}
            companies={companies}
            defaultCompanyId={companyId}
            onSaved={() => {
              toast({ tone: "success", title: t("common.saved") });
              void navigate({ to: "/cari/$id", params: { id } });
            }}
          />
        )
      }
    </CounterpartyPage>
  );
}
